import { formatDateByJurisdiction } from '@/features/register/utils/dateFormat';
import {
  formatMyAddressRowBodyForDisplay,
  type MyAddressDisplayContext,
  type MyResidentialFormSnapshot
} from '@/features/Contact/utils/contactMyLocationFields';
import type { Contact } from '@/features/Contact/types/contact.types';

export type GlobalContactMemberForm = {
  contactCd: string;
  categoryCd: string;
  contactName: string;
  identificationTypeCd: string;
  identificationNo: string;
  nationalityCd: string;
  dateOfBirth: string;
  gender: string;
  race: string;
  email: string;
  residential: MyAddressMemberFormSnapshot;
  service: MyAddressMemberFormSnapshot;
};

export const GLOBAL_CONTACT_MEMBER_FIELD_ORDER = [
  'person_name',
  'identification_type',
  'identification_number',
  'date_of_birth',
  'nationality',
  'gender',
  'race',
  'email',
  'residential_address',
  'service_address'
] as const;

export type GlobalContactMemberFieldKey = (typeof GLOBAL_CONTACT_MEMBER_FIELD_ORDER)[number];

export function globalContactFieldLabel(key: GlobalContactMemberFieldKey): string {
  const labels: Record<GlobalContactMemberFieldKey, string> = {
    person_name: 'Person Name',
    identification_type: 'Identification Type',
    identification_number: 'Identification Number',
    date_of_birth: 'Date of Birth',
    nationality: 'Nationality',
    gender: 'Gender',
    race: 'Race',
    email: 'Email',
    residential_address: 'Residential Address',
    service_address: 'Service Address'
  };
  return labels[key] || key;
}

function parseJson(raw: unknown): Record<string, unknown> {
  try {
    if (raw && typeof raw === 'string' && raw.trim().startsWith('{')) return JSON.parse(raw) || {};
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
  } catch {
    /* ignore */
  }
  return {};
}

function pickPrimaryAddressRow(rows: unknown): Record<string, unknown> | null {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const list = rows.filter((r) => r && typeof r === 'object') as Record<string, unknown>[];
  const primary = list.find((r) => {
    const f = r.isPrimaryAddress ?? r.is_primary_address;
    return f === 1 || f === '1' || f === true;
  });
  return primary || list[0] || null;
}

/** MY Contact 디테일과 동일 — 주소 행 동기화 플래그 */
export type MyAddressMemberFormSnapshot = MyResidentialFormSnapshot & {
  isSync?: boolean | number | string;
  addressId?: number;
  branchId?: number;
};

export function isAddressSyncFlag(addr: unknown): boolean {
  if (!addr || typeof addr !== 'object') return false;
  const v = (addr as Record<string, unknown>).IsSync ?? (addr as Record<string, unknown>).isSync ?? (addr as Record<string, unknown>).IS_SYNC;
  return v === 1 || v === '1' || v === true;
}

/** IsSync=1 이어도 코어 주소가 비어 있으면 Edit 허용 */
export function contactJsonAddressCoreEmpty(addr: unknown): boolean {
  if (!addr || typeof addr !== 'object') return true;
  const s = (v: unknown) => String(v ?? '').trim();
  const row = addr as Record<string, unknown>;
  const line1 = s(row.street01 ?? row.addressLine1);
  const line2 = s(row.street02 ?? row.addressLine2);
  const line3 = s(row.street03 ?? row.addressLine3);
  const pc = s(row.post_code ?? row.postcode ?? row.postalCode);
  const city = s(row.city);
  const state = s(row.state);
  const country = s(row.country ?? row.countryCode);
  return !line1 && !line2 && !line3 && !pc && !city && !state && !country;
}

/** 동기화된 주소이고 값이 있으면 Edit 불가 (Contact MY 디테일과 동일) */
export function contactJsonAddressSyncEditLocked(addr: unknown): boolean {
  return isAddressSyncFlag(addr) && !contactJsonAddressCoreEmpty(addr);
}

export function pickRegisterAddressIdFromRow(
  row: Record<string, unknown> | null | undefined,
): number | undefined {
  if (!row) return undefined;
  const id = Number(
    row.address_id
    ?? row.ADDRESS_ID
    ?? row.addressId
    ?? row.branch_id
    ?? row.BRANCH_ID
    ?? row.branchId
    ?? 0,
  );
  return Number.isFinite(id) && id > 0 ? id : undefined;
}

export function addressRowToMyForm(row: Record<string, unknown> | null | undefined): MyAddressMemberFormSnapshot {
  if (!row) {
    return { addressLine1: '', addressLine2: '', addressLine3: '', country: '', state: '', city: '', postcode: '' };
  }
  const postcodeRaw = row.postcodeCode || row.post_code || row.postcode || row.postalCode || '';
  const syncVal = row.IsSync ?? row.isSync ?? row.IS_SYNC;
  const addressId = pickRegisterAddressIdFromRow(row);
  const snapshot: MyAddressMemberFormSnapshot = {
    addressLine1: String(row.street01 || row.addressLine1 || '').trim(),
    addressLine2: String(row.street02 || row.addressLine2 || '').trim(),
    addressLine3: String(row.street03 || row.addressLine3 || '').trim(),
    country: String(row.countryCode || row.country || '').trim(),
    state: String(row.stateCode || row.state || '').trim(),
    city: String(row.cityCode || row.city || '').trim(),
    postcode: String(postcodeRaw).trim(),
  };
  if (addressId) {
    snapshot.addressId = addressId;
    snapshot.branchId = addressId;
  }
  if (syncVal !== undefined && syncVal !== null && syncVal !== '') {
    snapshot.isSync = syncVal as boolean | number | string;
  }
  return snapshot;
}

export function myFormToAddressPayload(
  form: MyResidentialFormSnapshot & { addressId?: number; branchId?: number },
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    addressLine1: form.addressLine1 || '',
    addressLine2: form.addressLine2 || '',
    addressLine3: form.addressLine3 || '',
    street01: form.addressLine1 || '',
    street02: form.addressLine2 || '',
    street03: form.addressLine3 || '',
    country: form.country || '',
    countryCode: form.country || '',
    state: form.state || '',
    stateCode: form.state || '',
    city: form.city || '',
    cityCode: form.city || '',
    post_code: form.postcode || '',
    postcode: form.postcode || '',
    postcodeCode: form.postcode || '',
    isPrimaryAddress: 1,
  };
  const addressId = Number(form.addressId ?? form.branchId ?? 0);
  if (Number.isFinite(addressId) && addressId > 0) {
    payload.address_id = addressId;
    payload.ADDRESS_ID = addressId;
    payload.branch_id = addressId;
    payload.BRANCH_ID = addressId;
  }
  return payload;
}

export function formatAddressDisplay(
  form: MyResidentialFormSnapshot,
  ctx: MyAddressDisplayContext = {}
): string {
  return formatMyAddressRowBodyForDisplay(
    {
      street01: form.addressLine1,
      street02: form.addressLine2,
      street03: form.addressLine3,
      country: form.country,
      countryCode: form.country,
      state: form.state,
      stateCode: form.state,
      city: form.city,
      cityCode: form.city,
      post_code: form.postcode,
      postcode: form.postcode,
      postcodeCode: form.postcode
    },
    ctx
  );
}

/** GET /Contact/:id 응답에서 contact 객체 추출 (apiClient는 axios response.data 반환) */
export function extractContactFromApiResponse(response: unknown): Contact | null {
  const raw = response as Record<string, unknown> | null | undefined;
  if (!raw) return null;
  const contact =
    (raw.contact as Contact | undefined) ??
    ((raw.data as Record<string, unknown> | undefined)?.contact as Contact | undefined) ??
    (raw.success === true && raw.CONTACT_CD ? (raw as unknown as Contact) : null) ??
    null;
  return contact && typeof contact === 'object' ? contact : null;
}

export function extractContactProfileFromApiResponse(response: unknown): {
  residentialAddress?: Record<string, unknown> | null;
  serviceAddress?: Record<string, unknown> | null;
  JSON_DATA?: unknown;
  CONTACT_JSON_DATA?: unknown;
} | null {
  const raw = response as Record<string, unknown> | null | undefined;
  if (!raw) return null;
  const payload =
    (raw.data as Record<string, unknown> | undefined)?.data ??
    raw.data ??
    raw;
  if (!payload || typeof payload !== 'object') return null;
  const p = payload as Record<string, unknown>;
  return {
    residentialAddress:
      (p.residentialAddress as Record<string, unknown> | null | undefined) ??
      (p.residential_address as Record<string, unknown> | null | undefined) ??
      null,
    serviceAddress:
      (p.serviceAddress as Record<string, unknown> | null | undefined) ??
      (p.service_address as Record<string, unknown> | null | undefined) ??
      null,
    JSON_DATA: p.JSON_DATA ?? p.jsonData,
    CONTACT_JSON_DATA: p.CONTACT_JSON_DATA ?? p.contactJsonData
  };
}

export function buildGlobalContactFormFromContact(
  contact: Contact | null | undefined,
  profilePayload?: {
    residentialAddress?: Record<string, unknown> | null;
    serviceAddress?: Record<string, unknown> | null;
    JSON_DATA?: unknown;
    CONTACT_JSON_DATA?: unknown;
  } | null,
  contactCdOverride?: string
): GlobalContactMemberForm | null {
  const cd = String(
    contact?.CONTACT_CD || (contact as any)?.contactCd || contactCdOverride || ''
  ).trim();
  if (!cd) return null;

  const jsonData = parseJson(profilePayload?.JSON_DATA);
  const contactJson = parseJson(profilePayload?.CONTACT_JSON_DATA);
  const profile = { ...jsonData, ...contactJson };

  let residentialRow = profilePayload?.residentialAddress as Record<string, unknown> | null;
  if (!residentialRow) {
    residentialRow = pickPrimaryAddressRow(
      (contact as any)?.residential_address ??
        (contact as any)?.registeredAddress ??
        profile.residential_address
    );
  }

  let serviceRow = profilePayload?.serviceAddress as Record<string, unknown> | null;
  if (!serviceRow) {
    const svcRaw =
      (contact as any)?.service_address ?? contactJson.service_address;
    serviceRow = pickPrimaryAddressRow(svcRaw);
  }

  const dobRaw = contact?.DATE_OF_BIRTH || (contact as any)?.dateOfBirth || '';
  const dob = dobRaw ? formatDateByJurisdiction(String(dobRaw).split('T')[0].split(' ')[0]) : '';

  const emails = (contact as any)?.emails || contact?.CONTACT_EMAIL;
  const email =
    Array.isArray(emails) && emails.length > 0
      ? String(emails[0] || '').trim()
      : String(contact?.CONTACT_EMAIL || (contact as any)?.contactEmail || '').trim();

  const residential = addressRowToMyForm(residentialRow);
  const service = addressRowToMyForm(serviceRow);

  const contactLevelSync =
    (contact as any)?.IS_SYNC ?? (contact as any)?.isSync ?? profile.is_sync ?? profile.IS_SYNC;
  if (contactLevelSync !== undefined && contactLevelSync !== null && contactLevelSync !== '') {
    if (!isAddressSyncFlag(residential)) {
      residential.isSync = contactLevelSync as boolean | number | string;
    }
  }

  return {
    contactCd: cd,
    categoryCd: String(contact?.CATEGORY_CD || (contact as any)?.categoryCd || '').trim(),
    contactName: String(contact?.CONTACT_NAME || (contact as any)?.contactName || '').trim(),
    identificationTypeCd: String(
      contact?.ID_TYPE_CD || (contact as any)?.idTypeCd || (contact as any)?.identificationType || ''
    ).trim(),
    identificationNo: String(contact?.CONTACT_REG_NO || (contact as any)?.contactRegNo || '').trim(),
    nationalityCd: String(contact?.NATIONALITY_CD || (contact as any)?.nationalityCd || '').trim(),
    dateOfBirth: dob,
    gender: String(
      (contact as any)?.profileGender || profile.gender || ''
    ).trim(),
    race: String((contact as any)?.profileRace || profile.race || '').trim(),
    email,
    residential,
    service
  };
}

export function buildContactUpdatePayload(
  form: GlobalContactMemberForm,
  entityCd?: string,
  contactNameOverride?: string
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    identificationType: form.identificationTypeCd || undefined,
    identificationNo: form.identificationNo || undefined,
    nationality: form.nationalityCd || undefined,
    dateOfBirth: form.dateOfBirth || undefined,
    profileGender: form.gender || undefined,
    profileRace: form.race || undefined,
    email: form.email || undefined,
    emails: form.email ? [form.email] : [],
    emailMainYn: form.email ? [true] : [],
    localAddress: {
      addressLine1: form.residential.addressLine1,
      addressLine2: form.residential.addressLine2,
      addressLine3: form.residential.addressLine3,
      country: form.residential.country,
      state: form.residential.state,
      city: form.residential.city,
      postcode: form.residential.postcode,
      post_code: form.residential.postcode
    },
    serviceAddressList: [myFormToAddressPayload(form.service)],
    skipResidentialJsonDataWrite: false
  };

  const name = String(contactNameOverride || form.contactName || '').trim();
  if (name) {
    payload.entityName = name;
  }

  const ent = String(entityCd || '').trim();
  if (ent) {
    payload.entityCd = ent;
    payload.branchEntityCd = ent;
  }

  return payload;
}
